import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  BiSolidSun,
  BiSolidMoon,
  BiLogOut,
} from "react-icons/bi";

import {
  FaUser,
  FaShoppingCart,
  FaHeart,
  FaSearch,
  FaCopy,
  FaChevronDown,
  FaTimes,
  FaChartLine,
  FaBars,
  FaChevronRight,
} from "react-icons/fa";

import axios from "axios";
import { useSelector, useDispatch } from "react-redux";

import AOS from "aos";
import "aos/dist/aos.css";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useGetTodosQuery } from "../../redux/slices/productApiSlice";
import { useGetsTodosQuery } from "../../redux/slices/todoApiSlice";
import { logout } from "../../redux/slices/authSlice";
import { useLogoutMutation } from "../../redux/slices/usersApiSlice";


const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const { userInfo } = useSelector((state) => state.auth);


  /*
   * =========================================================
   * DATA
   * =========================================================
   */

  const { data: cartData } = useGetTodosQuery();
  const { data: favoriteData } = useGetsTodosQuery();

  const cartItems =
    cartData?.todos ||
    cartData?.allTodos ||
    cartData ||
    [];

  const favorites =
    favoriteData?.todos ||
    favoriteData?.allTodos ||
    favoriteData ||
    [];

  const itemCount = Array.isArray(cartItems)
    ? cartItems.length
    : 0;

  const favoriteCount = Array.isArray(favorites)
    ? favorites.length
    : 0;


  /*
   * =========================================================
   * AOS
   * =========================================================
   */

  useEffect(() => {
    AOS.init({
      duration: 400,
      once: true,
      easing: "ease-out-cubic",
    });
  }, []);


  /*
   * =========================================================
   * MOBILE BODY SCROLL
   * =========================================================
   */

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);


  /*
   * =========================================================
   * ROUTE CHANGE
   * =========================================================
   */

  useEffect(() => {
    setIsMenuOpen(false);
    setIsProfileOpen(false);
    setSearchQuery("");
    setItems([]);
    setLoading(false);
  }, [location.pathname]);


  /*
   * =========================================================
   * CLICK OUTSIDE SEARCH
   * =========================================================
   */

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedDesktopSearch =
        desktopSearchRef.current &&
        desktopSearchRef.current.contains(event.target);

      const clickedMobileSearch =
        mobileSearchRef.current &&
        mobileSearchRef.current.contains(event.target);

      if (!clickedDesktopSearch && !clickedMobileSearch) {
        setItems([]);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside
      );
    };
  }, []);


  /*
   * =========================================================
   * SEARCH
   * =========================================================
   */

  useEffect(() => {
    const query = searchQuery.trim();

    if (!query) {
      setItems([]);
      setLoading(false);
      return;
    }

    const delay = setTimeout(async () => {
      try {
        setLoading(true);

        const [qolbaqResponse, paginationResponse] =
          await Promise.all([
            axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/qolbaq?limit=1000000`
            ),

            axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/pagination/getpagenations?search=${encodeURIComponent(
                query
              )}`
            ),
          ]);

        const qolbaqResults =
          qolbaqResponse.data?.allQolbaq ||
          qolbaqResponse.data?.qolbaqlar ||
          qolbaqResponse.data?.todos ||
          qolbaqResponse.data ||
          [];

        const paginationResults =
          paginationResponse.data?.allPagination ||
          paginationResponse.data?.pagination ||
          paginationResponse.data?.items ||
          paginationResponse.data ||
          [];

        const normalizedQuery =
          query.toLocaleLowerCase("az-AZ");

        const filteredQolbaq =
          Array.isArray(qolbaqResults)
            ? qolbaqResults.filter((item) => {
                const searchableText = [
                  item?.name,
                  item?.title,
                  item?.description,
                  item?.catagory,
                  item?.category,
                ]
                  .filter(
                    (value) =>
                      value !== undefined &&
                      value !== null
                  )
                  .join(" ")
                  .toLocaleLowerCase("az-AZ");

                return searchableText.includes(
                  normalizedQuery
                );
              })
            : [];

        const normalizedQolbaq =
          filteredQolbaq.map((item) => ({
            ...item,
            _searchType: "qolbaq",
          }));

        const normalizedPagination =
          Array.isArray(paginationResults)
            ? paginationResults.map((item) => ({
                ...item,
                _searchType: "pagination",
              }))
            : [];

        setItems([
          ...normalizedQolbaq,
          ...normalizedPagination,
        ]);
      } catch (error) {
        console.error(
          "Axtarış xətası:",
          error
        );

        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 350);

    return () => {
      clearTimeout(delay);
    };
  }, [searchQuery]);


  /*
   * =========================================================
   * LOGOUT
   * =========================================================
   */

  const [logoutApi] = useLogoutMutation();

  const handleLogout = async () => {
    try {
      await logoutApi().unwrap();

      dispatch(logout());

      setIsProfileOpen(false);
      setIsMenuOpen(false);

      toast.success(
        "Hesabdan uğurla çıxış edildi."
      );

      navigate("/login");
    } catch (error) {
      console.error(
        "Logout error:",
        error
      );

      toast.error(
        error?.data?.message ||
        "Çıxış zamanı xəta baş verdi."
      );
    }
  };


  /*
   * =========================================================
   * REFERRAL
   * =========================================================
   */

  const referralUrl = userInfo?.referralCode
    ? `https://artim.shop/register?referral=${userInfo.referralCode}`
    : "";

  const copyReferral = async () => {
    if (!referralUrl) return;

    try {
      await navigator.clipboard.writeText(
        referralUrl
      );

      toast.success(
        "Referral link kopyalandı."
      );
    } catch (error) {
      console.error(error);

      toast.error(
        "Link kopyalana bilmədi."
      );
    }
  };


  /*
   * =========================================================
   * NAVIGATION
   * =========================================================
   */

  const navLinks = [
    {
      name: "Məhsullar",
      path: "/products",
    },
    {
      name: "Haqqımızda",
      path: "/about",
    },
    {
      name: "Əlaqə",
      path: "/contact",
    },
    {
      name: "Sifarişlərim",
      path: "/confirmed",
    },
    {
      name: "Maaşlar",
      path: "/maas",
    },
    {
      name: "Maaş tarixçəsi",
      path: "/HistoryMaas",
    },
    {
      name: "Mükafatlar",
      path: "/qazanc",
    },
    {
      name: "Mükafat tarixçəsi",
      path: "/HistoryMukafat",
    },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };


  /*
   * =========================================================
   * SEARCH RESULT CLICK
   * =========================================================
   */

  const handleSearchResult = (item) => {
    const id =
      item?.qolbaq_id ||
      item?.pagination_id ||
      item?._id ||
      item?.id;

    if (!id) return;

    setSearchQuery("");
    setItems([]);
    setLoading(false);

    if (item?._searchType === "pagination") {
      navigate(
        `/pagination/${
          item?.pagination_id ||
          item?._id ||
          item?.id
        }`
      );

      return;
    }

    navigate(`/product/${id}`);
  };


  /*
   * =========================================================
   * CLEAR SEARCH
   * =========================================================
   */

  const clearSearch = () => {
    setSearchQuery("");
    setItems([]);
    setLoading(false);
  };


  /*
   * =========================================================
   * THEME
   * =========================================================
   */

  const [darkMode, setDarkMode] = useState(() => {
    return document.documentElement.classList.contains(
      "dark"
    );
  });


  const toggleTheme = () => {
    const html = document.documentElement;

    if (html.classList.contains("dark")) {
      html.classList.remove("dark");

      localStorage.setItem(
        "theme",
        "light"
      );

      setDarkMode(false);
    } else {
      html.classList.add("dark");

      localStorage.setItem(
        "theme",
        "dark"
      );

      setDarkMode(true);
    }
  };


  /*
   * =========================================================
   * THEME INITIALIZATION
   * =========================================================
   */

  useEffect(() => {
    const savedTheme =
      localStorage.getItem("theme");

    if (savedTheme === "dark") {
      document.documentElement.classList.add(
        "dark"
      );

      setDarkMode(true);
    }

    if (savedTheme === "light") {
      document.documentElement.classList.remove(
        "dark"
      );

      setDarkMode(false);
    }
  }, []);


  /*
   * =========================================================
   * SEARCH RESULTS
   * =========================================================
   */

  const SearchResults = ({
    mobile = false,
  }) => {
    if (!searchQuery.trim()) {
      return null;
    }

    return (
      <div
        className={`
          absolute
          ${
            mobile
              ? "top-[48px]"
              : "top-[58px]"
          }
          left-0
          right-0

          overflow-hidden

          rounded-2xl

          border
          border-gray-200
          dark:border-white/10

          bg-white
          dark:bg-gray-900

          shadow-2xl
          shadow-black/20

          z-30
        `}
      >
        {loading ? (
          <div className="p-5">
            <div className="flex items-center gap-3">
              <div
                className="
                  w-5
                  h-5
                  rounded-full
                  border-2
                  border-indigo-500
                  border-t-transparent
                  animate-spin
                  shrink-0
                "
              />

              <span
                className="
                  text-sm
                  text-gray-500
                  dark:text-gray-400
                "
              >
                Axtarılır...
              </span>
            </div>
          </div>
        ) : items.length > 0 ? (
          <div
            className="
              max-h-[420px]
              overflow-y-auto
              overscroll-contain
            "
          >
            {items
              .slice(0, 10)
              .map((item, index) => {
                const id =
                  item?._id ||
                  item?.qolbaq_id ||
                  item?.id ||
                  index;

                const getImageUrl = (value) => {
                  if (!value) return "";

                  if (typeof value === "string") {
                    return value;
                  }

                  if (Array.isArray(value)) {
                    const first =
                      value.find(Boolean);

                    return getImageUrl(first);
                  }

                  if (
                    typeof value === "object"
                  ) {
                    return (
                      value?.url ||
                      value?.secure_url ||
                      value?.path ||
                      value?.src ||
                      value?.image ||
                      ""
                    );
                  }

                  return "";
                };

                const image =
                  getImageUrl(item?.photo) ||
                  getImageUrl(item?.photos) ||
                  getImageUrl(item?.images) ||
                  getImageUrl(item?.image) ||
                  getImageUrl(item?.thumbnail) ||
                  getImageUrl(item?.imageUrl) ||
                  getImageUrl(item?.photoUrl);

                const title =
                  item?.name ||
                  item?.title ||
                  "Məhsul";

                return (
                  <button
                    type="button"
                    key={id}
                    onClick={() =>
                      handleSearchResult(item)
                    }
                    className="
                      w-full
                      flex
                      items-center
                      gap-3
                      p-3
                      text-left

                      hover:bg-gray-50
                      dark:hover:bg-white/[0.05]

                      active:bg-gray-100
                      dark:active:bg-white/[0.08]

                      transition
                    "
                  >
                    {/* IMAGE */}

                    <div
                      className="
                        w-12
                        h-12
                        shrink-0
                        rounded-xl
                        overflow-hidden

                        bg-gray-100
                        dark:bg-gray-800
                      "
                    >
                      {image ? (
                        <img
                          src={image}
                          alt={title}
                          className="
                            w-full
                            h-full
                            object-cover
                          "
                        />
                      ) : (
                        <div
                          className="
                            w-full
                            h-full
                            flex
                            items-center
                            justify-center
                            text-gray-400
                          "
                        >
                          <FaSearch />
                        </div>
                      )}
                    </div>

                    {/* INFO */}

                    <div
                      className="
                        min-w-0
                        flex-1
                      "
                    >
                      <div
                        className="
                          font-semibold
                          text-sm
                          truncate

                          text-gray-900
                          dark:text-white
                        "
                      >
                        {title}
                      </div>

                      <div
                        className="
                          mt-0.5
                          text-[10px]
                          font-medium
                          text-gray-400
                          dark:text-gray-500
                        "
                      >
                        {item?._searchType ===
                        "pagination"
                          ? "Elan"
                          : "Məhsul"}
                      </div>

                      {item?.price !==
                        undefined &&
                        item?.price !== null && (
                          <div
                            className="
                              mt-0.5
                              text-xs
                              font-semibold

                              text-indigo-600
                              dark:text-indigo-400
                            "
                          >
                            {item.price} ₼
                          </div>
                        )}
                    </div>

                    {/* ARROW */}

                    <FaChevronDown
                      className="
                        -rotate-90
                        text-gray-300
                        dark:text-gray-600
                        shrink-0
                      "
                      size={13}
                    />
                  </button>
                );
              })}
          </div>
        ) : (
          <div className="p-7 text-center">
            <div
              className="
                mx-auto
                mb-3
                w-12
                h-12
                rounded-2xl

                flex
                items-center
                justify-center

                bg-gray-100
                dark:bg-white/[0.06]

                text-gray-400
              "
            >
              <FaSearch />
            </div>

            <div
              className="
                font-semibold
                text-gray-800
                dark:text-white
              "
            >
              Məhsul tapılmadı
            </div>

            <div
              className="
                mt-1
                text-xs
                text-gray-400
              "
            >
              Başqa sözlə axtarmağı yoxla
            </div>
          </div>
        )}
      </div>
    );
  };


  /*
   * =========================================================
   * UI
   * =========================================================
   */

  return (
    <>
      {/* =====================================================
          DESKTOP + MOBILE HEADER
      ===================================================== */}

      <header
        className="
          fixed
          top-0
          left-0
          right-0

          z-20
        "
      >
        {/* ===================================================
            MAIN HEADER
        =================================================== */}

        <div
          className="
            relative
            z-20

            border-b
            border-gray-200/70
            dark:border-white/10

            bg-white/90
            dark:bg-gray-950/90

            backdrop-blur-2xl

            supports-[backdrop-filter]:bg-white/75
            dark:supports-[backdrop-filter]:bg-gray-950/75

            shadow-[0_8px_30px_rgba(0,0,0,0.04)]
            dark:shadow-[0_8px_30px_rgba(0,0,0,0.18)]
          "
        >
          <div
            className="
              w-full
              px-4
              sm:px-6
              lg:px-8
            "
          >
            {/* =================================================
                TOP ROW
            ================================================= */}

            <div
              className="
                h-[72px]

                flex
                items-center
                gap-3
                lg:gap-5
              "
            >
              {/* =================================================
                  LOGO
              ================================================= */}

              <Link
                to="/"
                onClick={() =>
                  setIsMenuOpen(false)
                }
                className="
                  shrink-0
                  flex
                  items-center
                  gap-3
                  group
                "
              >
                <div
                  className="
                    relative
                    w-11
                    h-11
                    rounded-2xl

                    flex
                    items-center
                    justify-center

                    bg-gradient-to-br
                    from-indigo-600
                    via-violet-600
                    to-fuchsia-600

                    shadow-lg
                    shadow-indigo-500/30

                    transition-all
                    duration-300

                    group-hover:scale-105
                    group-hover:shadow-indigo-500/50
                  "
                >
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                    />
                  </svg>

                  <span
                    className="
                      absolute
                      -top-1
                      -right-1
                      flex
                      h-3.5
                      w-3.5
                    "
                  >
                    <span
                      className="
                        animate-ping
                        absolute
                        inline-flex
                        h-full
                        w-full
                        rounded-full
                        bg-emerald-400
                        opacity-75
                      "
                    />

                    <span
                      className="
                        relative
                        inline-flex
                        rounded-full
                        h-3.5
                        w-3.5
                        bg-emerald-500
                        border-2
                        border-white
                        dark:border-gray-950
                      "
                    />
                  </span>
                </div>

                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    <span
                      className="
                        text-2xl
                        font-black
                        tracking-tight
                        text-gray-900
                        dark:text-white
                      "
                    >
                      Artım
                    </span>

                    <span
                      className="
                        text-2xl
                        font-black
                        tracking-tight
                        text-indigo-600
                        dark:text-indigo-400
                      "
                    >
                      Shop
                    </span>
                  </div>

                  <span
                    className="
                      -mt-1
                      text-[9px]
                      font-bold
                      uppercase
                      tracking-[0.2em]
                      text-gray-400
                      dark:text-gray-500
                    "
                  >
                    Al-Sat & Qazan
                  </span>
                </div>
              </Link>


              {/* =================================================
                  DESKTOP SEARCH
              ================================================= */}

              <div
                ref={desktopSearchRef}
                className="
                  hidden
                  md:block

                  flex-1
                  max-w-2xl
                  mx-auto

                  relative

                  z-50
                "
              >
                <div
                  className="
                    h-12

                    flex
                    items-center

                    bg-gray-100
                    dark:bg-white/[0.06]

                    border
                    border-transparent
                    dark:border-white/[0.06]

                    rounded-2xl

                    transition-all
                    duration-200

                    focus-within:bg-white
                    dark:focus-within:bg-gray-900

                    focus-within:border-indigo-500/30

                    focus-within:shadow-lg
                    focus-within:shadow-indigo-500/10
                  "
                >
                  <FaSearch
                    className="
                      ml-4
                      text-gray-400
                      dark:text-gray-500
                    "
                  />

                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) =>
                      setSearchQuery(
                        e.target.value
                      )
                    }
                    placeholder="Məhsul axtar..."
                    className="
                      flex-1
                      h-full
                      px-3

                      bg-transparent
                      outline-none
                      border-none

                      text-sm

                      text-gray-900
                      dark:text-white

                      placeholder:text-gray-400
                      dark:placeholder:text-gray-500
                    "
                  />

                  {searchQuery && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      className="
                        mr-2

                        w-8
                        h-8

                        rounded-xl

                        flex
                        items-center
                        justify-center

                        text-gray-400

                        hover:text-gray-700
                        dark:hover:text-white

                        hover:bg-gray-200
                        dark:hover:bg-white/10

                        transition
                      "
                    >
                      <FaTimes size={14} />
                    </button>
                  )}
                </div>

                <SearchResults />
              </div>


              {/* =================================================
                  RIGHT ACTIONS
              ================================================= */}

              <div
                className="
                  ml-auto

                  flex
                  items-center

                  gap-1.5
                  sm:gap-2
                "
              >
                {/* THEME */}

                <button
                  type="button"
                  onClick={toggleTheme}
                  aria-label="Tema dəyiş"
                  className="
                    w-10
                    h-10
                    sm:w-11
                    sm:h-11

                    rounded-xl
                    sm:rounded-2xl

                    flex
                    items-center
                    justify-center

                    text-gray-600
                    dark:text-gray-300

                    bg-gray-100
                    dark:bg-white/[0.06]

                    hover:bg-gray-200
                    dark:hover:bg-white/10

                    transition-all
                    duration-200

                    hover:scale-[1.03]
                  "
                >
                  {darkMode ? (
                    <BiSolidSun
                      size={20}
                      className="text-amber-400"
                    />
                  ) : (
                    <BiSolidMoon size={20} />
                  )}
                </button>


                {/* FAVORITES */}

                <Link
                  to="/favorie"
                  className="
                    relative

                    w-10
                    h-10
                    sm:w-11
                    sm:h-11

                    rounded-xl
                    sm:rounded-2xl

                    flex
                    items-center
                    justify-center

                    text-gray-600
                    dark:text-gray-300

                    bg-gray-100
                    dark:bg-white/[0.06]

                    hover:text-rose-500

                    hover:bg-rose-50
                    dark:hover:bg-rose-500/10

                    transition-all
                    duration-200
                  "
                >
                  <FaHeart size={17} />

                  {favoriteCount > 0 && (
                    <span
                      className="
                        absolute
                        -top-1
                        -right-1

                        min-w-[18px]
                        h-[18px]
                        px-1

                        rounded-full

                        flex
                        items-center
                        justify-center

                        bg-rose-500
                        text-white

                        text-[9px]
                        font-black

                        border-2
                        border-white
                        dark:border-gray-950
                      "
                    >
                      {favoriteCount > 99
                        ? "99+"
                        : favoriteCount}
                    </span>
                  )}
                </Link>


                {/* CART */}

                <Link
                  to="/basket"
                  className="
                    relative

                    w-10
                    h-10
                    sm:w-11
                    sm:h-11

                    rounded-xl
                    sm:rounded-2xl

                    flex
                    items-center
                    justify-center

                    text-gray-600
                    dark:text-gray-300

                    bg-gray-100
                    dark:bg-white/[0.06]

                    hover:text-indigo-600
                    dark:hover:text-indigo-400

                    hover:bg-indigo-50
                    dark:hover:bg-indigo-500/10

                    transition-all
                    duration-200
                  "
                >
                  <FaShoppingCart size={17} />

                  {itemCount > 0 && (
                    <span
                      className="
                        absolute
                        -top-1
                        -right-1

                        min-w-[18px]
                        h-[18px]
                        px-1

                        rounded-full

                        flex
                        items-center
                        justify-center

                        bg-indigo-600
                        text-white

                        text-[9px]
                        font-black

                        border-2
                        border-white
                        dark:border-gray-950
                      "
                    >
                      {itemCount > 99
                        ? "99+"
                        : itemCount}
                    </span>
                  )}
                </Link>


                {/* =================================================
                    DESKTOP AUTH / PROFILE
                ================================================= */}

                <div
                  className="
                    relative
                    z-40
                    hidden
                    lg:block
                  "
                >
                  {userInfo ? (
                    <>
                      {/* PROFILE BUTTON */}

                      <button
                        type="button"
                        onClick={() =>
                          setIsProfileOpen(
                            (prev) => !prev
                          )
                        }
                        className="
                          h-11

                          pl-2
                          pr-3

                          rounded-2xl

                          flex
                          items-center
                          gap-2.5

                          bg-gray-100
                          dark:bg-white/[0.06]

                          hover:bg-gray-200
                          dark:hover:bg-white/10

                          transition-all
                        "
                      >
                        <div
                          className="
                            w-8
                            h-8

                            rounded-full
                            overflow-hidden

                            flex
                            items-center
                            justify-center

                            bg-gradient-to-br
                            from-indigo-500
                            to-violet-600

                            text-white
                          "
                        >
                          {userInfo?.photo ? (
                            <img
                              src={userInfo.photo}
                              alt="Profil"
                              className="
                                w-full
                                h-full
                                rounded-full
                                object-cover
                                block
                              "
                            />
                          ) : (
                            <FaUser size={13} />
                          )}
                        </div>

                        <div
                          className="
                            hidden
                            xl:block
                            text-left
                          "
                        >
                          <div
                            className="
                              max-w-[100px]
                              truncate

                              text-xs
                              font-bold

                              text-gray-800
                              dark:text-white
                            "
                          >
                            {userInfo?.name ||
                              userInfo?.username ||
                              "Hesabım"}
                          </div>

                          <div
                            className="
                              text-[9px]
                              text-gray-400
                            "
                          >
                            Profil
                          </div>
                        </div>

                        <FaChevronDown
                          size={10}
                          className={`
                            text-gray-400
                            transition-transform

                            ${
                              isProfileOpen
                                ? "rotate-180"
                                : ""
                            }
                          `}
                        />
                      </button>


                      {/* PROFILE DROPDOWN */}

                      {isProfileOpen && (
                        <div
                          data-aos="fade-down"
                          className="
                            absolute
                            top-[56px]
                            right-0

                            w-[310px]

                            overflow-hidden

                            rounded-3xl

                            border
                            border-gray-200
                            dark:border-white/10

                            bg-white
                            dark:bg-gray-900

                            shadow-2xl
                            shadow-black/10

                            z-[999999]
                          "
                        >
                          {/* PROFILE HEADER */}

                          <div
                            className="
                              p-5

                              bg-gradient-to-br
                              from-indigo-600
                              via-violet-600
                              to-fuchsia-600

                              text-white
                            "
                          >
                            <div
                              className="
                                flex
                                items-center
                                gap-3
                              "
                            >
                              <div
                                className="
                                  w-12
                                  h-12

                                  rounded-2xl
                                  overflow-hidden

                                  bg-white/20
                                  backdrop-blur

                                  flex
                                  items-center
                                  justify-center

                                  border
                                  border-white/20
                                "
                              >
                                {userInfo?.photo ? (
                                  <img
                                    src={userInfo.photo}
                                    alt="Profil"
                                    className="
                                      w-full
                                      h-full
                                      object-cover
                                    "
                                  />
                                ) : (
                                  <FaUser size={18} />
                                )}
                              </div>

                              <div
                                className="
                                  min-w-0
                                "
                              >
                                <div
                                  className="
                                    font-bold
                                    truncate
                                  "
                                >
                                  {userInfo?.name ||
                                    userInfo?.username ||
                                    "İstifadəçi"}
                                </div>

                                <div
                                  className="
                                    mt-0.5

                                    text-xs
                                    text-white/70

                                    truncate
                                  "
                                >
                                  {userInfo?.email ||
                                    "Hesabınız"}
                                </div>
                              </div>
                            </div>
                          </div>


                          {/* PROFILE LINKS */}

                          <div className="p-2">
                            <Link
                              to="/profile"
                              onClick={() =>
                                setIsProfileOpen(false)
                              }
                              className="
                                flex
                                items-center
                                gap-3

                                px-3
                                py-3

                                rounded-2xl

                                text-sm
                                font-medium

                                text-gray-700
                                dark:text-gray-200

                                hover:bg-gray-100
                                dark:hover:bg-white/[0.06]

                                transition
                              "
                            >
                              <FaUser
                                className="text-indigo-500"
                              />

                              Profilim
                            </Link>

                            <Link
                              to="/confirmed"
                              onClick={() =>
                                setIsProfileOpen(false)
                              }
                              className="
                                flex
                                items-center
                                gap-3

                                px-3
                                py-3

                                rounded-2xl

                                text-sm
                                font-medium

                                text-gray-700
                                dark:text-gray-200

                                hover:bg-gray-100
                                dark:hover:bg-white/[0.06]

                                transition
                              "
                            >
                              <FaShoppingCart
                                className="text-indigo-500"
                              />

                              Sifarişlərim
                            </Link>
                          </div>


                          {/* REFERRAL */}

                          {referralUrl && (
                            <div
                              className="
                                mx-3
                                mb-3
                                p-3

                                rounded-2xl

                                bg-indigo-50
                                dark:bg-indigo-500/10

                                border
                                border-indigo-100
                                dark:border-indigo-500/20
                              "
                            >
                              <div
                                className="
                                  flex
                                  items-center
                                  justify-between
                                  gap-2
                                "
                              >
                                <div
                                  className="
                                    min-w-0
                                  "
                                >
                                  <div
                                    className="
                                      text-[10px]
                                      uppercase
                                      tracking-wider
                                      font-bold

                                      text-indigo-500
                                    "
                                  >
                                    Referral link
                                  </div>

                                  <div
                                    className="
                                      mt-1

                                      text-xs
                                      text-gray-500
                                      dark:text-gray-400

                                      truncate
                                    "
                                  >
                                    Dostlarını dəvət et
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={copyReferral}
                                  className="
                                    shrink-0

                                    w-9
                                    h-9

                                    rounded-xl

                                    flex
                                    items-center
                                    justify-center

                                    bg-indigo-600
                                    text-white

                                    hover:bg-indigo-700

                                    transition
                                  "
                                >
                                  <FaCopy size={13} />
                                </button>
                              </div>
                            </div>
                          )}


                          {/* LOGOUT */}

                          <div
                            className="
                              p-2
                              pt-0
                            "
                          >
                            <button
                              type="button"
                              onClick={handleLogout}
                              className="
                                w-full

                                flex
                                items-center
                                gap-3

                                px-3
                                py-3

                                rounded-2xl

                                text-sm
                                font-semibold

                                text-red-500

                                hover:bg-red-50
                                dark:hover:bg-red-500/10

                                transition
                              "
                            >
                              <BiLogOut size={19} />

                              Çıxış et
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    /* =================================================
                       LOGIN / REGISTER
                    ================================================= */

                    <div
                      className="
                        flex
                        items-center
                        gap-2
                      "
                    >
                      <Link
                        to="/login"
                        className="
                          h-11
                          px-4

                          rounded-2xl

                          flex
                          items-center
                          justify-center

                          text-sm
                          font-semibold

                          text-gray-700
                          dark:text-gray-200

                          bg-gray-100
                          dark:bg-white/[0.06]

                          hover:bg-gray-200
                          dark:hover:bg-white/10

                          transition
                        "
                      >
                        Giriş et
                      </Link>

                      <Link
                        to="/register"
                        className="
                          h-11
                          px-4

                          rounded-2xl

                          flex
                          items-center
                          justify-center

                          text-sm
                          font-bold

                          bg-indigo-600
                          text-white

                          shadow-lg
                          shadow-indigo-500/20

                          hover:bg-indigo-700

                          transition
                        "
                      >
                        Qeydiyyatdan keç
                      </Link>
                    </div>
                  )}
                </div>


                {/* =================================================
                    MOBILE MENU BUTTON
                ================================================= */}

                <button
                  type="button"
                  onClick={() =>
                    setIsMenuOpen(true)
                  }
                  className="
                    lg:hidden

                    w-10
                    h-10
                    sm:w-11
                    sm:h-11

                    rounded-xl
                    sm:rounded-2xl

                    flex
                    items-center
                    justify-center

                    bg-gray-100
                    dark:bg-white/[0.06]

                    text-gray-700
                    dark:text-gray-200

                    hover:bg-gray-200
                    dark:hover:bg-white/10

                    transition
                  "
                >
                  <FaBars size={19} />
                </button>
              </div>
            </div>


            {/* =================================================
                MOBILE SEARCH
            ================================================= */}

            <div
              ref={mobileSearchRef}
              className="
                md:hidden

                pb-3

                relative

                z-50
              "
            >
              <div
                className="
                  h-11

                  rounded-xl

                  flex
                  items-center

                  bg-gray-100
                  dark:bg-white/[0.06]

                  border
                  border-transparent
                  dark:border-white/[0.06]
                "
              >
                <FaSearch
                  className="
                    ml-3
                    text-gray-400
                  "
                  size={14}
                />

                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) =>
                    setSearchQuery(
                      e.target.value
                    )
                  }
                  placeholder="Məhsul axtar..."
                  className="
                    flex-1

                    h-full

                    px-3

                    bg-transparent

                    outline-none

                    text-sm

                    text-gray-900
                    dark:text-white

                    placeholder:text-gray-400
                    dark:placeholder:text-gray-500
                  "
                />

                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="
                      mr-2

                      w-8
                      h-8

                      rounded-lg

                      flex
                      items-center
                      justify-center

                      text-gray-400

                      hover:text-gray-700
                      dark:hover:text-white

                      hover:bg-gray-200
                      dark:hover:bg-white/10
                    "
                  >
                    <FaTimes size={14} />
                  </button>
                )}
              </div>

              <SearchResults mobile />
            </div>
          </div>
        </div>


        {/* =====================================================
            DESKTOP NAVIGATION
        ===================================================== */}

        <div
          className="
            hidden
            lg:block

            relative
            z-10

            w-full

            bg-white
            dark:bg-gray-950

            border-b
            border-gray-100
            dark:border-white/[0.06]
          "
        >
          <div
            className="
              w-full
              px-4
              sm:px-6
              lg:px-8
            "
          >
            <nav
              className="
                min-h-12

                flex
                items-center

                gap-1

                overflow-x-auto

                scrollbar-hide
              "
            >
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`
                    relative

                    shrink-0

                    h-9

                    px-3.5

                    rounded-xl

                    flex
                    items-center
                    justify-center

                    text-xs
                    font-semibold

                    transition-all

                    ${
                      isActive(link.path)
                        ? `
                          text-indigo-600
                          dark:text-indigo-400

                          bg-indigo-50
                          dark:bg-indigo-500/10
                        `
                        : `
                          text-gray-500
                          dark:text-gray-400

                          hover:text-gray-900
                          dark:hover:text-white

                          hover:bg-gray-50
                          dark:hover:bg-white/[0.04]
                        `
                    }
                  `}
                >
                  {link.name}

                  {isActive(link.path) && (
                    <span
                      className="
                        absolute

                        bottom-0.5
                        left-1/2

                        -translate-x-1/2

                        w-5
                        h-0.5

                        rounded-full

                        bg-indigo-600
                        dark:bg-indigo-400
                      "
                    />
                  )}
                </Link>
              ))}
            </nav>
          </div>
        </div>


        {/* =====================================================
            CATEGORY BAR
        ===================================================== */}

        <div
          className="
            w-full

            bg-gray-50/95
            dark:bg-gray-900/95

            border-b
            border-gray-100
            dark:border-white/[0.05]

            backdrop-blur

            relative

            z-0
          "
        />
      </header>


      {/* =====================================================
          MOBILE FULLSCREEN MENU
      ===================================================== */}

      {isMenuOpen && (
        <div
          className="
            fixed
            inset-0

            z-[1000000]

            lg:hidden

            w-screen
            h-screen

            overflow-hidden

            bg-white
            dark:bg-gray-950
          "
        >
          {/* =================================================
              MOBILE MENU HEADER
          ================================================= */}

          <div
            className="
              h-[72px]

              px-4
              sm:px-6

              flex
              items-center
              justify-between

              border-b
              border-gray-100
              dark:border-white/[0.06]
            "
          >
            <Link
              to="/"
              onClick={() =>
                setIsMenuOpen(false)
              }
              className="
                flex
                items-center
                gap-2.5
              "
            >
              <div
                className="
                  w-10
                  h-10

                  rounded-xl

                  flex
                  items-center
                  justify-center

                  bg-gradient-to-br
                  from-indigo-600
                  via-violet-600
                  to-fuchsia-600

                  text-white
                "
              >
                <FaChartLine />
              </div>

              <div>
                <div
                  className="
                    text-lg
                    font-black

                    text-gray-900
                    dark:text-white
                  "
                >
                  ARTIM
                </div>

                <div
                  className="
                    text-[8px]
                    font-bold
                    uppercase
                    tracking-[0.2em]

                    text-indigo-500
                  "
                >
                  Marketplace
                </div>
              </div>
            </Link>

            <button
              type="button"
              onClick={() =>
                setIsMenuOpen(false)
              }
              className="
                w-11
                h-11

                rounded-2xl

                flex
                items-center
                justify-center

                bg-gray-100
                dark:bg-white/[0.06]

                text-gray-600
                dark:text-gray-300
              "
            >
              <FaTimes size={20} />
            </button>
          </div>


          {/* =================================================
              MOBILE MENU BODY
          ================================================= */}

          <div
            className="
              h-[calc(100vh-72px)]

              overflow-y-auto

              overscroll-contain

              px-4
              sm:px-6

              py-5
              pb-10
            "
          >
            {/* =================================================
                USER / AUTH CARD
            ================================================= */}

            {userInfo ? (
              <div
                className="
                  p-4

                  rounded-3xl

                  bg-gradient-to-br
                  from-indigo-600
                  via-violet-600
                  to-fuchsia-600

                  text-white

                  shadow-xl
                  shadow-indigo-500/15
                "
              >
                <Link
                  to="/profile"
                  onClick={() =>
                    setIsMenuOpen(false)
                  }
                  className="
                    flex
                    items-center
                    justify-between
                    gap-3
                    w-full
                  "
                >
                  <div
                    className="
                      w-10
                      h-10

                      rounded-full
                      overflow-hidden

                      shrink-0

                      flex
                      items-center
                      justify-center

                      bg-white/20
                      backdrop-blur

                      text-white

                      border
                      border-white/20
                    "
                  >
                    {userInfo?.photo ? (
                      <img
                        src={userInfo.photo}
                        alt="Profil"
                        className="
                          w-full
                          h-full
                          rounded-full
                          object-cover
                          block
                        "
                      />
                    ) : (
                      <FaUser size={15} />
                    )}
                  </div>

                  <div
                    className="
                      min-w-0
                      flex-1
                    "
                  >
                    <div
                      className="
                        font-bold
                        truncate
                      "
                    >
                      {userInfo?.name ||
                        userInfo?.username ||
                        "Xoş gəlmisiniz"}
                    </div>

                    <div
                      className="
                        mt-0.5

                        text-xs
                        text-white/70

                        truncate
                      "
                    >
                      {userInfo?.email ||
                        "ARTIM hesabınız"}
                    </div>
                  </div>

                  <FaChevronRight
                    size={13}
                    className="
                      shrink-0
                      text-white/70
                    "
                  />
                </Link>
              </div>
            ) : (
              <div
                className="
                  p-4

                  rounded-3xl

                  bg-gradient-to-br
                  from-indigo-600
                  via-violet-600
                  to-fuchsia-600

                  text-white

                  shadow-xl
                  shadow-indigo-500/15
                "
              >
                <div
                  className="
                    flex
                    items-center
                    gap-3
                  "
                >
                  <div
                    className="
                      w-11
                      h-11

                      rounded-2xl

                      flex
                      items-center
                      justify-center

                      bg-white/15
                      backdrop-blur

                      border
                      border-white/20
                    "
                  >
                    <FaUser size={17} />
                  </div>

                  <div
                    className="
                      min-w-0
                      flex-1
                    "
                  >
                    <div className="font-bold">
                      Xoş gəlmisiniz
                    </div>

                    <div
                      className="
                        mt-0.5

                        text-xs
                        text-white/70
                      "
                    >
                      Hesabınıza daxil olun və ya
                      qeydiyyatdan keçin
                    </div>
                  </div>
                </div>

                <div
                  className="
                    grid
                    grid-cols-2
                    gap-2

                    mt-4
                  "
                >
                  <Link
                    to="/login"
                    onClick={() =>
                      setIsMenuOpen(false)
                    }
                    className="
                      h-11

                      rounded-xl

                      flex
                      items-center
                      justify-center

                      bg-white/15
                      backdrop-blur

                      border
                      border-white/20

                      text-sm
                      font-semibold

                      hover:bg-white/25

                      transition
                    "
                  >
                    Giriş et
                  </Link>

                  <Link
                    to="/register"
                    onClick={() =>
                      setIsMenuOpen(false)
                    }
                    className="
                      h-11

                      rounded-xl

                      flex
                      items-center
                      justify-center

                      bg-white

                      text-indigo-600

                      text-sm
                      font-bold

                      hover:bg-gray-100

                      transition
                    "
                  >
                    Qeydiyyatdan keç
                  </Link>
                </div>
              </div>
            )}


            {/* =================================================
                QUICK ACTIONS
            ================================================= */}

            <div
              className="
                grid
                grid-cols-2

                gap-3

                mt-4
              "
            >
              <Link
                to="/favorie"
                onClick={() =>
                  setIsMenuOpen(false)
                }
                className="
                  relative

                  p-4

                  rounded-2xl

                  bg-gray-50
                  dark:bg-white/[0.05]

                  border
                  border-gray-100
                  dark:border-white/[0.06]

                  flex
                  items-center
                  gap-3
                "
              >
                <div
                  className="
                    w-10
                    h-10

                    rounded-xl

                    flex
                    items-center
                    justify-center

                    bg-rose-50
                    dark:bg-rose-500/10

                    text-rose-500
                  "
                >
                  <FaHeart size={16} />
                </div>

                <div>
                  <div
                    className="
                      text-xs
                      text-gray-400
                    "
                  >
                    Favorilər
                  </div>

                  <div
                    className="
                      font-bold

                      text-gray-900
                      dark:text-white
                    "
                  >
                    {favoriteCount}
                  </div>
                </div>
              </Link>


              <Link
                to="/basket"
                onClick={() =>
                  setIsMenuOpen(false)
                }
                className="
                  p-4

                  rounded-2xl

                  bg-gray-50
                  dark:bg-white/[0.05]

                  border
                  border-gray-100
                  dark:border-white/[0.06]

                  flex
                  items-center
                  gap-3
                "
              >
                <div
                  className="
                    w-10
                    h-10

                    rounded-xl

                    flex
                    items-center
                    justify-center

                    bg-indigo-50
                    dark:bg-indigo-500/10

                    text-indigo-500
                  "
                >
                  <FaShoppingCart size={16} />
                </div>

                <div>
                  <div
                    className="
                      text-xs
                      text-gray-400
                    "
                  >
                    Səbət
                  </div>

                  <div
                    className="
                      font-bold

                      text-gray-900
                      dark:text-white
                    "
                  >
                    {itemCount}
                  </div>
                </div>
              </Link>
            </div>


            {/* =================================================
                NAVIGATION
            ================================================= */}

            <div className="mt-6">
              <div
                className="
                  px-1
                  mb-2

                  text-[10px]
                  uppercase
                  tracking-[0.15em]

                  font-bold

                  text-gray-400
                "
              >
                Menyu
              </div>

              <div className="space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() =>
                      setIsMenuOpen(false)
                    }
                    className={`
                      flex
                      items-center
                      justify-between

                      px-4
                      py-3.5

                      rounded-2xl

                      text-sm
                      font-semibold

                      transition

                      ${
                        isActive(link.path)
                          ? `
                            bg-indigo-50
                            dark:bg-indigo-500/10

                            text-indigo-600
                            dark:text-indigo-400
                          `
                          : `
                            text-gray-700
                            dark:text-gray-200

                            hover:bg-gray-50
                            dark:hover:bg-white/[0.05]
                          `
                      }
                    `}
                  >
                    <span>
                      {link.name}
                    </span>

                    <FaChevronDown
                      size={10}
                      className="
                        -rotate-90
                        text-gray-300
                      "
                    />
                  </Link>
                ))}
              </div>
            </div>


            {/* =================================================
                REFERRAL
            ================================================= */}

            {userInfo && referralUrl && (
              <div
                className="
                  mt-6

                  p-4

                  rounded-3xl

                  bg-indigo-50
                  dark:bg-indigo-500/10

                  border
                  border-indigo-100
                  dark:border-indigo-500/20
                "
              >
                <div
                  className="
                    flex
                    items-center
                    justify-between
                    gap-3
                  "
                >
                  <div>
                    <div
                      className="
                        text-sm
                        font-bold

                        text-indigo-700
                        dark:text-indigo-300
                      "
                    >
                      Dostunu dəvət et
                    </div>

                    <div
                      className="
                        mt-1

                        text-xs

                        text-indigo-500/70
                      "
                    >
                      Referral linkini paylaş
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={copyReferral}
                    className="
                      shrink-0

                      w-11
                      h-11

                      rounded-2xl

                      flex
                      items-center
                      justify-center

                      bg-indigo-600
                      text-white

                      shadow-lg
                      shadow-indigo-500/20
                    "
                  >
                    <FaCopy size={14} />
                  </button>
                </div>
              </div>
            )}


            {/* =================================================
                THEME
            ================================================= */}

            <button
              type="button"
              onClick={toggleTheme}
              className="
                mt-4

                w-full

                flex
                items-center
                justify-between

                px-4
                py-4

                rounded-2xl

                bg-gray-50
                dark:bg-white/[0.05]

                border
                border-gray-100
                dark:border-white/[0.06]
              "
            >
              <div
                className="
                  flex
                  items-center
                  gap-3
                "
              >
                <div
                  className="
                    w-10
                    h-10

                    rounded-xl

                    flex
                    items-center
                    justify-center

                    bg-gray-200
                    dark:bg-white/10
                  "
                >
                  {darkMode ? (
                    <BiSolidSun
                      className="text-amber-400"
                    />
                  ) : (
                    <BiSolidMoon />
                  )}
                </div>

                <div className="text-left">
                  <div
                    className="
                      text-sm
                      font-semibold

                      text-gray-800
                      dark:text-white
                    "
                  >
                    Görünüş
                  </div>

                  <div
                    className="
                      text-xs
                      text-gray-400
                    "
                  >
                    {darkMode
                      ? "Qaranlıq rejim"
                      : "Açıq rejim"}
                  </div>
                </div>
              </div>

              <div
                className="
                  w-10
                  h-6

                  rounded-full

                  bg-gray-300
                  dark:bg-indigo-600

                  relative
                "
              >
                <span
                  className={`
                    absolute

                    top-1

                    w-4
                    h-4

                    rounded-full

                    bg-white

                    shadow

                    transition-all

                    ${
                      darkMode
                        ? "left-5"
                        : "left-1"
                    }
                  `}
                />
              </div>
            </button>


            {/* =================================================
                MOBILE LOGOUT
            ================================================= */}

            {userInfo && (
              <button
                type="button"
                onClick={handleLogout}
                className="
                  mt-3

                  w-full

                  flex
                  items-center
                  justify-center
                  gap-2

                  px-4
                  py-4

                  rounded-2xl

                  bg-red-50
                  dark:bg-red-500/10

                  text-red-500

                  font-semibold
                  text-sm

                  border
                  border-red-100
                  dark:border-red-500/10
                "
              >
                <BiLogOut size={19} />

                Hesabdan çıx
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
